import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import Contact from "../ui/Contact.js";
import NewsletterSignup from "../ui/NewsletterSignup.js";
import styles from "./BlogPost.module.css";
import { preloaded, capture } from "../ui/preloadedState.js";

function parseFrontmatter(raw) {
  const match = raw.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
  if (!match) return { meta: {}, body: raw };

  const meta = {};
  match[1].split("\n").forEach((line) => {
    const idx = line.indexOf(":");
    if (idx === -1) return;
    const key = line.slice(0, idx).trim();
    let val = line.slice(idx + 1).trim();
    if (
      (val.startsWith('"') && val.endsWith('"')) ||
      (val.startsWith("'") && val.endsWith("'"))
    ) {
      val = val.slice(1, -1);
    }
    meta[key] = val;
  });

  return { meta, body: match[2] };
}

export default function BlogPost({ languageEn }) {
  const { slug } = useParams();

  const lang = languageEn ? "en" : "bg";
  const prefix = languageEn ? "/en" : "";

  // Seeded from the prerender — see src/ui/preloadedState.js. Without it the
  // first client render is the loading placeholder while the HTML holds the
  // whole article, and React discards the prerendered page.
  const preloadedPost = preloaded(`post:${lang}:${slug}`);
  const [meta, setMeta] = useState(preloadedPost ? preloadedPost.meta : null);
  const [body, setBody] = useState(preloadedPost ? preloadedPost.body : "");
  const [notFound, setNotFound] = useState(false);

  const text = {
    en: {
      back: "Back to Blog",
      notFound: "Post not found.",
    },
    bg: {
      back: "Обратно към блога",
      notFound: "Публикацията не е намерена.",
    },
  };
  const t = languageEn ? text.en : text.bg;

  useEffect(() => {
    fetch(`/content/blog/${lang}/${slug}.md`)
      .then((res) => {
        if (!res.ok) throw new Error("not found");
        return res.text();
      })
      .then((raw) => {
        const { meta: m, body: b } = parseFrontmatter(raw);
        capture(`post:${lang}:${slug}`, { meta: m, body: b });
        setMeta(m);
        setBody(b);
      })
      .catch(() => setNotFound(true));
  }, [lang, slug]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString(languageEn ? "en-GB" : "bg-BG", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (notFound) {
    return (
      <div className={styles.wrap}>
        <div className={styles.container}>
          <p className={styles.notFound}>{t.notFound}</p>
          <Link to={`${prefix}/blog`} className={styles.backLink}>
            {`← ${t.back}`}
          </Link>
        </div>
      </div>
    );
  }

  if (!meta) {
    return <div className={styles.wrap}><div className={styles.loading} /></div>;
  }

  const thumbUrl = meta.thumbnail
    ? `https://vkushty.com${meta.thumbnail.startsWith("/") ? "" : "/"}${meta.thumbnail}`
    : null;

  return (
    <div className={styles.wrap}>
      <Helmet defer={false}>
        <html lang={languageEn ? "en" : "bg"} />
        <title>{meta.title} - Vkushty</title>
        {(meta.noindex === "true" || meta.noindex === true) && (
          <meta name="robots" content="noindex, follow" />
        )}
        <meta name="description" content={meta.description || ""} />
        <link
          rel="canonical"
          href={`https://vkushty.com${prefix}/blog/${slug}/`}
        />
        <meta property="og:site_name" content="Vkushty" />
        <meta property="og:title" content={`${meta.title} - Vkushty`} />
        <meta property="og:description" content={meta.description || ""} />
        <meta property="og:url" content={`https://vkushty.com${prefix}/blog/${slug}/`} />
        <meta property="og:type" content="article" />
        <meta property="og:locale" content={languageEn ? "en_US" : "bg_BG"} />
        <meta property="og:locale:alternate" content={languageEn ? "bg_BG" : "en_US"} />
        {thumbUrl && <meta property="og:image" content={thumbUrl} />}
        {thumbUrl && <meta property="og:image:width" content="1200" />}
        {thumbUrl && <meta property="og:image:height" content="866" />}
        {thumbUrl && <meta property="og:image:type" content="image/jpeg" />}
        {thumbUrl && <meta property="og:image:alt" content={meta.thumbnailAlt || meta.title} />}
        {meta.date && <meta property="article:published_time" content={meta.date} />}
        <meta name="twitter:card" content={thumbUrl ? "summary_large_image" : "summary"} />
        <meta name="twitter:title" content={`${meta.title} - Vkushty`} />
        <meta name="twitter:description" content={meta.description || ""} />
        {thumbUrl && <meta name="twitter:image" content={thumbUrl} />}
        {thumbUrl && <meta name="twitter:image:alt" content={meta.thumbnailAlt || meta.title} />}
        <link rel="alternate" hreflang="bg" href={`https://vkushty.com/blog/${slug}/`} />
        <link rel="alternate" hreflang="en" href={`https://vkushty.com/en/blog/${slug}/`} />
        <link rel="alternate" hreflang="x-default" href={`https://vkushty.com/blog/${slug}/`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: meta.title,
            description: meta.description || "",
            datePublished: meta.date || undefined,
            dateModified: meta.date || undefined,
            ...(thumbUrl ? { image: thumbUrl } : {}),
            url: `https://vkushty.com${prefix}/blog/${slug}/`,
            author: {
              "@type": "Organization",
              name: "Vkushty",
              url: "https://vkushty.com",
            },
            publisher: {
              "@type": "Organization",
              name: "Vkushty",
              url: "https://vkushty.com",
              logo: {
                "@type": "ImageObject",
                url: "https://vkushty.com/logo512.png",
              },
            },
          })}
        </script>
      </Helmet>

      <div id="snap-ready" style={{ display: "none" }} />
      <div className={styles.container}>
        <Link to={`${prefix}/blog`} className={styles.backLink}>
          {`← ${t.back}`}
        </Link>

        <article className={styles.article}>
          <header className={styles.header}>
            <time className={styles.date}>{formatDate(meta.date)}</time>
            <h1 className={styles.title}>{meta.title}</h1>
            {meta.description && (
              <p className={styles.subtitle}>{meta.description}</p>
            )}
          </header>

          {meta.thumbnail && (
            <div className={styles.heroImage}>
              <img src={meta.thumbnail} alt={meta.thumbnailAlt || meta.title} />
            </div>
          )}

          <div className={styles.body}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                // ![caption](https://youtube.com/shorts/<id>) embeds the video
                // as a portrait player; any other image renders as usual. A
                // <span>, not a <div>: markdown puts images inside a <p>.
                img: ({ src, alt, ...props }) => {
                  const yt = /youtu(?:\.be\/|be\.com\/(?:shorts\/|watch\?v=|embed\/))([\w-]{11})/.exec(src || "");
                  if (!yt) return <img src={src} alt={alt} {...props} />;
                  return (
                    <span className={styles.video}>
                      <iframe
                        src={`https://www.youtube.com/embed/${yt[1]}`}
                        title={alt}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        loading="lazy"
                      />
                    </span>
                  );
                },
                a: ({ href, children, ...props }) => {
                  const isExternal = href && (href.startsWith("http") || href.startsWith("//"));
                  return isExternal ? (
                    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                      {children}
                    </a>
                  ) : (
                    <a href={href} {...props}>{children}</a>
                  );
                },
              }}
            >
              {body}
            </ReactMarkdown>
          </div>
        </article>

        <NewsletterSignup languageEn={languageEn} source="blog_post" variant="inline" />

        {(meta.showContact === "true" || meta.showContact === true) && (
          <Contact languageEn={languageEn} />
        )}
      </div>
    </div>
  );
}
